import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LINK_INTENT_KEY = "wylo_link_google_intent";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser, refresh, persistTokens } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }

    const linkIntent = localStorage.getItem(LINK_INTENT_KEY) === "1";
    localStorage.removeItem(LINK_INTENT_KEY);

    (async () => {
      try {
        if (linkIntent) {
          // Link a Google identity to the currently signed-in user.
          const { data } = await api.post("/auth/link-google", { session_id: sessionId });
          setUser(data);
          toast.success("Google account linked.");
          window.history.replaceState(null, "", window.location.pathname);
          nav("/app/settings", { replace: true });
          return;
        }
        // Fresh Google sign-in
        const { data } = await api.post("/auth/google-session", { session_id: sessionId });
        persistTokens?.(data);
        setUser(data);
        toast.success(`Welcome, ${data.name?.split(" ")[0] || "to WYLO"}.`);
        window.history.replaceState(null, "", window.location.pathname);
        const to = data.onboarding_completed ? "/app" : "/onboarding";
        nav(to, { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Google sign-in failed. Please try again.";
        toast.error(typeof msg === "string" ? msg : "Google sign-in failed. Please try again.");
        if (linkIntent) {
          await refresh();
          nav("/app/settings", { replace: true });
        } else {
          nav("/login", { replace: true });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
      <div className="flex flex-col items-center gap-3" data-testid="auth-callback-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#E8ECF2] border-t-[#1A3A5C] animate-spin" />
        <p className="text-sm text-[#555568]">Signing you in…</p>
      </div>
    </div>
  );
}
